import { isMetaAdLibraryViewOnlyItem } from "../../../services/competitorAdsGallery";
import {
  COMPETITOR_FAVORITES_FOLDER_ID,
  COMPETITOR_FOLDER_CONTENT_ID_SEPARATOR,
  CONTENT_FOLDER_ROOT_ID,
  VIDEO_FORMATS,
} from "../../../utils/constants";
import { formatUrlMedia, getExtensionFile } from "../../../utils/string";

export const encodeCompetitorFolderContentID = ({ pageId, libraryID }) => {
  const normalizedPageId = String(pageId || "").trim();
  const normalizedLibraryID = String(libraryID ?? "").trim();

  if (!normalizedPageId || !normalizedLibraryID) return null;

  return `${normalizedPageId}${COMPETITOR_FOLDER_CONTENT_ID_SEPARATOR}${normalizedLibraryID}`;
};

export const decodeCompetitorFolderContentID = (contentID) => {
  const value = String(contentID || "").trim();
  if (!value) return { pageId: null, libraryID: null };

  const separatorIndex = value.indexOf(COMPETITOR_FOLDER_CONTENT_ID_SEPARATOR);
  if (separatorIndex <= 0) {
    return { pageId: null, libraryID: value };
  }

  return {
    pageId: value.slice(0, separatorIndex),
    libraryID: value.slice(
      separatorIndex + COMPETITOR_FOLDER_CONTENT_ID_SEPARATOR.length
    ),
  };
};

export const getCompetitorFolderContentID = (item) =>
  encodeCompetitorFolderContentID({
    pageId: item?.pageId,
    libraryID: item?.libraryID ?? item?.id,
  });

export const getCompetitorItemId = (item) => {
  const folderContentID = getCompetitorFolderContentID(item);
  if (folderContentID) return folderContentID;

  if (!item) return null;
  return String(item.libraryID ?? item.id ?? "").trim() || null;
};

export const isCompetitorItemViewOnly = (item) =>
  isMetaAdLibraryViewOnlyItem(item);

export const getCompetitorReferenceMediaType = (type) =>
  type === "video" ? "video" : "image";

export const isCompetitorMediaAllowedForReference = (
  type,
  acceptedReferenceMediaType
) => {
  const mediaType = getCompetitorReferenceMediaType(type);
  if (acceptedReferenceMediaType === "video") return mediaType === "video";
  if (acceptedReferenceMediaType === "image") return mediaType === "image";
  return !isMetaAdLibraryViewOnlyItem({ type: mediaType });
};

export const isCompetitorItemSelectable = (
  item,
  { acceptedReferenceMediaType } = {}
) => {
  if (!item?.id) return false;
  if (!acceptedReferenceMediaType) {
    return !isCompetitorItemViewOnly(item);
  }
  return isCompetitorMediaAllowedForReference(
    item.type,
    acceptedReferenceMediaType
  );
};

export const findCompetitorItemById = ({ items, itemId }) => {
  if (!itemId || !Array.isArray(items)) return null;
  return items.find((item) => getCompetitorItemId(item) === itemId) ?? null;
};

export const findCompetitorItemIndexById = ({ items, itemId }) => {
  if (!itemId || !Array.isArray(items)) return -1;
  return items.findIndex((item) => getCompetitorItemId(item) === itemId);
};

const hasCompetitorAdText = (value) => Boolean(String(value || "").trim());

const hasCompetitorAdLibraryCopy = (item) =>
  hasCompetitorAdText(item?.description) ||
  hasCompetitorAdText(item?.headline) ||
  hasCompetitorAdText(item?.headlineSecondary) ||
  hasCompetitorAdText(item?.cta);

export const hasCompetitorAdLibraryLayout = (item) =>
  (Array.isArray(item?.additionalMedia) && item.additionalMedia.length > 0) ||
  hasCompetitorAdLibraryCopy(item);

export const buildCompetitorAdMediaVersions = ({ item }) => {
  if (!item?.url) return [];

  const mainVersion = {
    url: item.url,
    type: item.type === "video" ? "video" : "image",
    ...(Number(item.width) > 0 ? { width: Number(item.width) } : {}),
    ...(Number(item.height) > 0 ? { height: Number(item.height) } : {}),
  };

  const seenUrls = new Set([mainVersion.url]);
  const versions = [mainVersion];

  (item.additionalMedia || []).forEach((entry) => {
    const url = typeof entry?.url === "string" ? entry.url.trim() : "";
    if (!url || seenUrls.has(url)) return;

    seenUrls.add(url);
    versions.push({
      url,
      type: entry?.type === "video" ? "video" : "image",
      ...(Number(entry?.width) > 0 ? { width: Number(entry.width) } : {}),
      ...(Number(entry?.height) > 0 ? { height: Number(entry.height) } : {}),
    });
  });

  return versions;
};

const isCompetitorItemVideoMedia = ({ url, type }) => {
  if (type === "video") return true;
  if (type === "image") return false;
  if (!url) return false;
  if (url.startsWith("data:video/")) return true;
  return VIDEO_FORMATS.includes(getExtensionFile(url));
};

export const getCompetitorFavoriteToastMedia = (item) => {
  if (!item) return { url: "", type: "image" };

  const mainUrl = typeof item.url === "string" ? item.url.trim() : "";
  const mainIsVideo = isCompetitorItemVideoMedia({
    url: mainUrl,
    type: item.type,
  });

  if (mainUrl && mainIsVideo) {
    return {
      url: formatUrlMedia(mainUrl),
      type: "video",
    };
  }

  if (mainUrl) {
    return { url: formatUrlMedia(mainUrl), type: "image" };
  }

  const versions = buildCompetitorAdMediaVersions({ item });
  const firstVersion = versions[0];
  if (firstVersion?.url) {
    return {
      url: formatUrlMedia(firstVersion.url),
      type: firstVersion.type === "video" ? "video" : "image",
    };
  }

  const preview = typeof item.preview === "string" ? item.preview.trim() : "";
  if (preview) {
    return { url: formatUrlMedia(preview), type: "image" };
  }

  return { url: "", type: "image" };
};

export const buildCompetitorFoldersForBar = ({
  folders = [],
  favoritesLabel,
}) => [
  {
    id: COMPETITOR_FAVORITES_FOLDER_ID,
    name: favoritesLabel,
    parentFolderID: CONTENT_FOLDER_ROOT_ID,
    isSystem: true,
  },
  ...folders,
];

export const isValidCompetitorFavoriteDestination = ({
  folderID,
  foldersByID = {},
}) => {
  const normalizedFolderID = String(folderID || "").trim();
  if (!normalizedFolderID) return false;
  if (normalizedFolderID === COMPETITOR_FAVORITES_FOLDER_ID) return true;
  return Boolean(foldersByID[normalizedFolderID]);
};

export const formatCompetitorAdCtaLabel = (cta) => {
  const value = String(cta || "").trim();
  if (!value) return "";

  if (/^[A-Z0-9_]+$/.test(value)) {
    return value
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  }

  return value;
};
